import { useState } from "react";

import { ArrowLeft } from "lucide-react";

import type { TaskDetail } from "@domain/task-state";

import { TaskCheck } from "./TaskCheck";
import { relativeTime, TYPE_LABEL, TYPE_PRESENTATION } from "./typePresentation";

const STATE_LABEL: Readonly<Record<TaskDetail["displayState"], string>> = {
  todo: "To do",
  working: "Working now",
  held: "Waiting on you",
  done: "Done",
};

const META_LABEL =
  "font-body text-[11px] font-bold tracking-[0.14em] text-home-ink/40 dark:text-white/40 uppercase";

/** Absolute timestamp for the detail meta rows (the list shows relative only). */
function absoluteTime(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function renderOutputValue(value: unknown): string {
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

export interface TaskDetailViewProps {
  /** `null` = not found (missing, foreign, non-task-surface, or hidden). */
  task: TaskDetail | null;
  isLoading: boolean;
  /** Navigate back to the task list. Threaded from the route layer. */
  onBack: () => void;
  /** Injected for deterministic relative-time rendering; defaults to Date.now(). */
  now?: number;
  /**
   * Relay a complete intent (praxis-intent-relay §3.7) — surfaces
   * through the shared TaskCheck for todo/working non-decision tasks.
   */
  onComplete?: () => void;
  /** Relay a decision answer — decision form on held decision tasks. */
  onResolveDecision?: (answer: string) => void | Promise<void>;
  /** A relayed write for this task is in flight. */
  pendingWrite?: boolean;
  /** Inline relay error copy (failed / unconfirmed). */
  relayError?: string | null;
}

/**
 * Detail page for a single task (`/tasks/$taskId`). Pure — props in,
 * JSX out; the query lives in `useTask`, the write relays in
 * `useTaskWrites`. Write affordances: the shared TaskCheck completes
 * todo/working tasks; held decisions carry the answer form.
 */
export function TaskDetailView({
  task,
  isLoading,
  onBack,
  now,
  onComplete,
  onResolveDecision,
  pendingWrite,
  relayError,
}: TaskDetailViewProps) {
  const nowMs = now ?? Date.now();

  return (
    <div className="mx-auto min-w-0 max-w-2xl">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 rounded-lg px-1.5 py-1 -ml-1.5 font-body text-[13px] font-semibold text-home-ink/55 dark:text-white/55 transition-colors hover:text-home-ink dark:hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={2.4} />
        Tasks
      </button>

      {isLoading ? (
        <p className="mt-10 text-center font-body text-[13px] text-home-ink/45 dark:text-white/45">
          Loading task…
        </p>
      ) : task === null ? (
        <div className="mt-10 flex flex-col items-center gap-2 text-center">
          <p className="font-body text-[14px] font-semibold text-home-ink/70 dark:text-white/70">
            Task not found
          </p>
          <p className="font-body text-[13px] text-home-ink/45 dark:text-white/45">
            It may have been archived, or the link is stale.
          </p>
        </div>
      ) : (
        <TaskDetailBody
          task={task}
          nowMs={nowMs}
          onComplete={onComplete}
          onResolveDecision={onResolveDecision}
          pendingWrite={pendingWrite}
          relayError={relayError}
        />
      )}
    </div>
  );
}

function TaskDetailBody({
  task,
  nowMs,
  onComplete,
  onResolveDecision,
  pendingWrite,
  relayError,
}: {
  task: TaskDetail;
  nowMs: number;
  onComplete?: () => void;
  onResolveDecision?: (answer: string) => void | Promise<void>;
  pendingWrite?: boolean;
  relayError?: string | null;
}) {
  const [answer, setAnswer] = useState("");
  const { icon: Icon, tone } = TYPE_PRESENTATION[task.type];

  // Server-computed predicate (same one the relay mutation guards on);
  // a stale click still bounces off INVALID_STATE.
  const completable = task.completable && onComplete !== undefined;
  const showDecisionForm =
    task.heldReason === "decision" && onResolveDecision !== undefined;
  const busy = pendingWrite === true;
  const canRecord = showDecisionForm && !busy && answer.trim().length > 0;

  const heldHint =
    task.heldReason === "decision"
      ? showDecisionForm
        ? "Needs a decision from you."
        : "Needs a decision from you — reply in chat to decide."
      : task.heldReason === "waiting_for"
        ? "Waiting on someone else."
        : null;

  return (
    <div className="mt-6">
      <div className="flex items-start gap-3">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone}`}
        >
          <Icon className="h-5 w-5" strokeWidth={2.4} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-[22px] font-semibold leading-snug tracking-tight text-home-ink dark:text-white">
            {task.title}
          </h2>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1.5 rounded-full bg-home-ink/[0.06] dark:bg-white/[0.08] px-2.5 py-1 font-body text-[11.5px] font-semibold text-home-ink/70 dark:text-white/70">
              <TaskCheck
                state={task.displayState}
                pending={busy ? true : undefined}
                {...(completable
                  ? { onComplete, taskTitle: task.title }
                  : {})}
              />
              {STATE_LABEL[task.displayState]}
            </span>
            <span className="rounded-full bg-home-ink/[0.06] dark:bg-white/[0.08] px-2.5 py-1 font-body text-[11.5px] font-semibold text-home-ink/70 dark:text-white/70">
              {TYPE_LABEL[task.type]}
            </span>
            <span className="font-body text-[11.5px] text-home-ink/40 dark:text-white/40">
              {task.shortId}
            </span>
          </div>
        </div>
      </div>

      {relayError != null ? (
        <p className="mt-4 font-body text-[12.5px] text-home-terracotta">
          {relayError}
        </p>
      ) : null}

      {heldHint !== null ? (
        <div className="mt-5 rounded-2xl bg-home-butter/50 dark:bg-white/5 p-4">
          <p className="font-body text-[13px] text-home-ink/75 dark:text-white/75">
            {heldHint}
          </p>
          {task.waiter ? (
            <span className="mt-2.5 inline-block rounded-full bg-white dark:bg-surface-dark-card px-3.5 py-1.5 font-body text-[12px] font-semibold text-home-ink dark:text-white shadow-[0_6px_16px_-11px_rgba(33,31,28,0.5)]">
              {task.waiter}
            </span>
          ) : null}
          {showDecisionForm ? (
            <div className="mt-3">
              <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                disabled={busy}
                rows={3}
                placeholder="Type your decision…"
                aria-label={`Decision for "${task.title}"`}
                className="w-full resize-none rounded-xl border border-home-ink/10 dark:border-white/10 bg-white dark:bg-surface-dark-card px-3 py-2 font-body text-[13px] text-home-ink dark:text-white placeholder:text-home-ink/40 dark:placeholder:text-white/40 outline-none focus:border-home-ink/30 dark:focus:border-white/30 disabled:opacity-60"
              />
              <button
                type="button"
                onClick={() => {
                  if (canRecord && onResolveDecision !== undefined) {
                    void onResolveDecision(answer.trim());
                  }
                }}
                disabled={!canRecord}
                aria-busy={busy}
                className="mt-2 rounded-full bg-home-ink dark:bg-white px-3.5 py-1.5 font-body text-[12px] font-semibold text-white dark:text-home-ink transition-opacity disabled:opacity-30"
              >
                {busy ? "Recording…" : "Record decision"}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {task.description ? (
        <div className="mt-5">
          <p className={META_LABEL}>Details</p>
          <p className="mt-2 whitespace-pre-wrap font-body text-[14px] leading-relaxed text-home-ink/80 dark:text-white/80">
            {task.description}
          </p>
        </div>
      ) : null}

      {task.decisionOutput !== undefined ? (
        <div className="mt-5">
          <p className={META_LABEL}>Decided</p>
          <div className="mt-2 rounded-2xl border border-home-ink/8 dark:border-white/8 bg-white dark:bg-surface-dark-card p-4">
            {Object.keys(task.decisionOutput).length === 0 ? (
              <p className="font-body text-[13px] text-home-ink/55 dark:text-white/55">
                Resolved with no recorded outputs.
              </p>
            ) : (
              <dl className="space-y-2">
                {Object.entries(task.decisionOutput).map(([key, value]) => (
                  <div key={key} className="flex flex-wrap items-baseline gap-x-3">
                    <dt className="font-body text-[12px] font-semibold text-home-ink/55 dark:text-white/55">
                      {key}
                    </dt>
                    <dd className="min-w-0 font-body text-[13px] text-home-ink/85 dark:text-white/85">
                      {renderOutputValue(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </div>
      ) : null}

      <div className="mt-8 border-t border-home-ink/8 dark:border-white/8 pt-4">
        <dl className="space-y-1.5">
          <div className="flex items-baseline gap-x-3">
            <dt className="w-20 shrink-0 font-body text-[12px] text-home-ink/45 dark:text-white/45">
              Updated
            </dt>
            <dd
              className="font-body text-[12.5px] text-home-ink/70 dark:text-white/70"
              title={absoluteTime(task.updatedAt)}
            >
              {relativeTime(task.updatedAt, nowMs)}
            </dd>
          </div>
          <div className="flex items-baseline gap-x-3">
            <dt className="w-20 shrink-0 font-body text-[12px] text-home-ink/45 dark:text-white/45">
              Created
            </dt>
            <dd className="font-body text-[12.5px] text-home-ink/70 dark:text-white/70">
              {absoluteTime(task.createdAt)}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
